import { Controller } from "@hotwired/stimulus";

/**
 * Reading Menu Canvas Controller (Restaurant
 * Audit, Step 5).
 *
 * Drives the "Reporte de salud online" canvas.
 *
 * Architecture (CAL-264). The reading_menu canvas is
 * rendered ONCE on page load and never swapped by
 * `audit-stage`. All state is value-driven from
 * Stimulus values that the server updates via
 * `set_dataset_attributes` Turbo Stream actions.
 *
 * Lifecycle:
 *
 * 1. **connect()**: read every value, fill the
 *    visible DOM. If the payload is already narrated
 *    (page loaded into a completed audit), the
 *    report is fully visible immediately.
 *
 * 2. **`*ValueChanged`**: fires when narration
 *    arrives via dataset broadcast. Re-render the
 *    affected section in place.
 *
 * 3. When the visible content has been rendered for
 *    a narrated payload, dispatch
 *    `audit-step:complete` and stamp
 *    `data-audit-step-complete` on the closest
 *    stage. The Continue button for `reading_menu`
 *    is hidden by audit-continue-button (terminal
 *    affordance is the report's own CTA), but the
 *    event fires for consistency with the other
 *    steps.
 */
export default class extends Controller {
  static values = {
    narrated: { type: String, default: "false" },
    score: { type: String, default: "" },
    tierLine: { type: String, default: "" },
    bucketPillsHtml: {
      type: Array,
      default: [],
    },
    lossKind: { type: String, default: "default" },
    lossAmount: { type: String, default: "" },
    lossAmountInteger: { type: String, default: "0" },
    lossParagraph: { type: String, default: "" },
    qualitativeLoss: { type: String, default: "" },
    problemsCountLabel: {
      type: String,
      default: "",
    },
    problemsState: {
      type: String,
      default: "running",
    },
    problemCardsHtml: {
      type: Array,
      default: [],
    },
    rankingIntro: { type: String, default: "" },
    rankingState: {
      type: String,
      default: "empty",
    },
    rankingRowsHtml: {
      type: Array,
      default: [],
    },
  };

  static targets = [
    "score",
    "tierLine",
    "bucketPills",
    "costBody",
    "problemsCount",
    "problemCards",
    "rankingIntro",
    "rankingRows",
  ];

  connect() {
    this.completeDispatched = false;
    this.syncAll();
    this.maybeMarkComplete();
  }

  // -------- value-changed reactors --------

  narratedValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncAll();
    this.maybeMarkComplete();
  }

  scoreValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncScore();
  }

  tierLineValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncTierLine();
  }

  bucketPillsHtmlValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (
      JSON.stringify(value) ===
      JSON.stringify(previousValue)
    )
      return;
    this.syncBucketPills();
  }

  lossKindValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncCostBody();
  }

  lossAmountValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncCostBody();
  }

  lossAmountIntegerValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncCostBody();
  }

  lossParagraphValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncCostBody();
  }

  qualitativeLossValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncCostBody();
  }

  problemsCountLabelValueChanged(
    value,
    previousValue
  ) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncProblemsCount();
  }

  problemsStateValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncProblemCards();
  }

  problemCardsHtmlValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (
      JSON.stringify(value) ===
      JSON.stringify(previousValue)
    )
      return;
    this.syncProblemCards();
  }

  rankingIntroValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncRankingIntro();
  }

  rankingStateValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncRankingRows();
  }

  rankingRowsHtmlValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (
      JSON.stringify(value) ===
      JSON.stringify(previousValue)
    )
      return;
    this.syncRankingRows();
  }

  // -------- sync from values --------

  syncAll() {
    this.syncScore();
    this.syncTierLine();
    this.syncBucketPills();
    this.syncCostBody();
    this.syncProblemsCount();
    this.syncProblemCards();
    this.syncRankingIntro();
    this.syncRankingRows();
  }

  syncScore() {
    if (!this.hasScoreTarget) return;
    // The score span is wired with audit-count-up; we
    // drive the animation by mutating its to-value
    // attribute rather than overwriting textContent.
    // The count-up controller's toValueChanged
    // callback picks up the change and replays the
    // tween from the currently displayed number.
    this.scoreTarget.setAttribute(
      "data-audit-count-up-to-value",
      this.scoreValue
    );
  }

  syncTierLine() {
    if (!this.hasTierLineTarget) return;
    this.tierLineTarget.textContent =
      this.tierLineValue;
  }

  syncBucketPills() {
    if (!this.hasBucketPillsTarget) return;
    const html = this.bucketPillsHtmlValue;
    if (!Array.isArray(html) || html.length === 0) {
      return;
    }
    this.bucketPillsTarget.innerHTML = html.join("");
  }

  syncCostBody() {
    if (!this.hasCostBodyTarget) return;
    let html = "";
    if (this.lossKindValue === "numeric") {
      html =
        this.buildAmountHtml(
          this.lossAmountIntegerValue
        ) +
        this.buildLossParagraphHtml(
          this.lossParagraphValue
        );
    } else {
      html = this.buildQualitativeHtml(
        this.qualitativeLossValue
      );
    }
    this.costBodyTarget.innerHTML = html;
  }

  // The injected markup re-uses the audit-count-up
  // controller. Stimulus's MutationObserver wires it
  // up automatically when the innerHTML lands, so the
  // tween plays for the running -> painted transition
  // just like it does on full-page-load of an
  // already-narrated audit. Mirror the server-side
  // markup from `render_loss_amount` exactly so the
  // visual result is identical between the two paths.
  buildAmountHtml(integerValue) {
    return (
      '<div class="flex items-baseline gap-2 ' +
      'flex-wrap">' +
      '<span class="font-[Inter,system-ui,' +
      "sans-serif] text-[72px] leading-[76px] " +
      "tracking-[-0.025em] font-semibold " +
      'text-base-content" ' +
      'data-testid="report-loss-amount">' +
      "<span>~$</span>" +
      "<span " +
      'data-controller="audit-count-up" ' +
      'data-audit-count-up-from-value="0" ' +
      'data-audit-count-up-to-value="' +
      this.escape(integerValue) +
      '" ' +
      'data-audit-count-up-delay-value="0.2" ' +
      'data-audit-count-up-formatter-value=' +
      '"thousands">' +
      "0</span>" +
      "</span>" +
      '<span class="text-[14px] leading-[18px] ' +
      "uppercase tracking-[0.14em] " +
      "font-semibold " +
      'text-base-content/55">MXN / mes</span>' +
      "</div>"
    );
  }

  buildLossParagraphHtml(text) {
    return (
      '<p class="max-w-[640px] text-[16px] ' +
      "leading-[24px] " +
      'text-[#3A3A36]">' +
      this.escape(text) +
      "</p>"
    );
  }

  buildQualitativeHtml(text) {
    return (
      '<p class="max-w-[640px] text-[18px] ' +
      "leading-[28px] tracking-[-0.005em] " +
      'font-medium text-base-content">' +
      this.escape(text) +
      "</p>"
    );
  }

  syncProblemsCount() {
    if (!this.hasProblemsCountTarget) return;
    this.problemsCountTarget.textContent =
      this.problemsCountLabelValue;
  }

  syncProblemCards() {
    if (!this.hasProblemCardsTarget) return;
    const state = this.problemsStateValue;
    if (state === "filled") {
      const cards = this.problemCardsHtmlValue;
      const wrapped =
        '<div class="flex flex-col gap-3.5">' +
        (Array.isArray(cards) ? cards.join("") : "") +
        "</div>";
      this.problemCardsTarget.innerHTML = wrapped;
    } else if (state === "clean") {
      this.problemCardsTarget.innerHTML =
        this.cleanProblemsHtml();
    } else {
      this.problemCardsTarget.innerHTML =
        this.runningProblemsHtml();
    }
  }

  cleanProblemsHtml() {
    return (
      '<div class="rounded-2xl bg-base-100 ' +
      "border border-[#E5E1D6] " +
      "px-6 py-5 flex items-start gap-3\" " +
      'data-testid="report-problems-clean">' +
      '<div class="shrink-0 w-7 h-7 rounded-full ' +
      "bg-[#FDE047] flex items-center " +
      "justify-center text-[14px] " +
      'font-semibold text-base-content">✓</div>' +
      '<div class="flex flex-col gap-1">' +
      '<div class="text-[16px] leading-5 ' +
      'font-semibold text-base-content">' +
      "Sin problemas detectados.</div>" +
      '<div class="text-[14px] leading-5 ' +
      'text-base-content/55">' +
      "Tu presencia online ya cubre los puntos " +
      "básicos. Sigue así.</div>" +
      "</div>" +
      "</div>"
    );
  }

  runningProblemsHtml() {
    return (
      '<div class="rounded-2xl bg-base-100 ' +
      "border border-[#E5E1D6] " +
      "px-6 py-5 text-[14px] leading-5 " +
      'text-base-content/55" ' +
      'data-testid="report-problems-running">' +
      "Aún calculando tu reporte. Las cosas para " +
      "arreglar aparecerán aquí en unos segundos." +
      "</div>"
    );
  }

  syncRankingIntro() {
    if (!this.hasRankingIntroTarget) return;
    this.rankingIntroTarget.textContent =
      this.rankingIntroValue;
  }

  syncRankingRows() {
    if (!this.hasRankingRowsTarget) return;
    if (this.rankingStateValue === "filled") {
      const rows = this.rankingRowsHtmlValue;
      this.rankingRowsTarget.innerHTML =
        '<div class="flex flex-col">' +
        (Array.isArray(rows) ? rows.join("") : "") +
        "</div>";
    } else {
      this.rankingRowsTarget.innerHTML =
        this.emptyRankingHtml();
    }
  }

  emptyRankingHtml() {
    return (
      '<div class="px-6 py-5 text-[14px] ' +
      'leading-5 text-base-content/55">' +
      "Calculando el ranking de competidores." +
      "</div>"
    );
  }

  // -------- completion signaling --------

  maybeMarkComplete() {
    if (this.completeDispatched) return;
    if (this.narratedValue !== "true") return;

    this.completeDispatched = true;

    const stage = this.element.closest(
      '[data-audit-stage-target="stage"]'
    );
    if (stage) {
      stage.dataset.auditStepComplete = "true";
    }
    document.dispatchEvent(
      new CustomEvent("audit-step:complete", {
        detail: { stepKey: "reading_menu" },
      })
    );
  }

  // -------- helpers --------

  escape(text) {
    const div = document.createElement("div");
    div.textContent = text == null ? "" : text;
    return div.innerHTML;
  }
}
